import RequirePricingAdminUser from '@/components/auth/RequirePricingAdminUser';
import DateTimeDisplay from '@/components/DateTime';
import {
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { PricingSettingStatus } from 'types';

import { currencyFormatter } from '../../../../../helpers/currencyFormatter';
import {
  useDeleteOrganizationSiteSurveyPricingMutation,
  useGetOrganizationSiteSurveyPricingQuery,
} from '../../../../../services/pricing';
import { TableLoading } from '../../../../TableLoading';

import { CreateSiteSurvey } from './modals/CreateSiteSurvey';

interface SiteSurveyPricingRow {
  id: string;
  organizationId: string;
  organizationName: string;
  state: string;
  programType: string;
  status: PricingSettingStatus;
  value: { amount: number };
  createdBy?: { user?: { id: string; name: string }; date?: string };
  archivedBy?: { user?: { id: string; name: string }; date?: string };
}

export const SiteSurvey = () => {
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { data, isLoading, isFetching } = useGetOrganizationSiteSurveyPricingQuery(
    { alias, programType },
    { skip: !alias || !programType },
  );
  const [deleteSiteSurveyPricingMutation] = useDeleteOrganizationSiteSurveyPricingMutation();

  const siteSurveys = useMemo(() => {
    const list = (data?.data ?? []) as SiteSurveyPricingRow[];
    if (list.length === 0) {
      return [];
    }
    const byStatus = list.reduce(
      (acc, row) => {
        acc[row.status] = acc[row.status] || [];
        acc[row.status].push(row);
        return acc;
      },
      {} as Record<string, SiteSurveyPricingRow[]>,
    );
    const getCreatedDateMs = (row: SiteSurveyPricingRow) =>
      row.createdBy?.date ? new Date(row.createdBy.date).getTime() : 0;
    const sortByCreatedDateDesc = (a: SiteSurveyPricingRow, b: SiteSurveyPricingRow) =>
      getCreatedDateMs(b) - getCreatedDateMs(a);

    return [
      ...(byStatus[PricingSettingStatus.active] || []).toSorted(sortByCreatedDateDesc),
      ...(byStatus[PricingSettingStatus.archived] || []).toSorted(sortByCreatedDateDesc),
    ];
  }, [data]);

  const handleArchive = async (id: string) => {
    try {
      await deleteSiteSurveyPricingMutation({ alias, id, programType }).unwrap();
      toast.success('Site Survey pricing archived successfully');
    } catch (e) {
      console.error(e);
      toast.error('Error archiving Site Survey pricing');
    }
  };

  const columnConfig: Column[] = [
    {
      heading: 'status',
      dataKey: 'status',
      render: (cell: any) => (
        <Badge
          variant={
            cell === PricingSettingStatus.active
              ? 'success'
              : cell === PricingSettingStatus.archived
                ? 'warning'
                : 'danger'
          }
          message={cell}
        />
      ),
    },
    { heading: 'State', dataKey: 'state' },
    {
      heading: 'Amount',
      dataKey: 'value',
      render: (cell: any) => currencyFormatter.format(cell?.amount ?? 0),
    },
    { heading: 'org', dataKey: 'organizationName' },
    {
      heading: 'Created',
      dataKey: 'createdBy',
      width: 150,
      render: (cell: any) =>
        cell?.date ? <DateTimeDisplay value={cell.date} dateFormat={'DD'} excludeTime /> : null,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedBy',
      width: 150,
      render: (cell: any) =>
        cell?.date ? <DateTimeDisplay value={cell.date} dateFormat={'DD'} excludeTime /> : null,
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        const isOwnedByOrg = row.organizationId === alias;
        return (
          row.status === PricingSettingStatus.active &&
          isOwnedByOrg && (
            <RequirePricingAdminUser>
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                onClick={() => {
                  handleArchive(row.id);
                }}
              >
                Archive
              </Button>
            </RequirePricingAdminUser>
          )
        );
      },
    },
  ];

  return (
    <>
      <CreateSiteSurvey isModalOpen={isModalOpen} closeModal={closeModal} />
      <Card.Header>
        <RequirePricingAdminUser>
          <Box direction="row" justifyContent="space-between">
            <Box />
            <Button
              as="button"
              variant="primary"
              iconPrefix="add"
              size={{ base: 'sm', desktop: 'md' }}
              aria-label="add site survey pricing"
              onClick={() => openModal()}
            >
              {!isPhone && 'Site Survey'}
            </Button>
          </Box>
        </RequirePricingAdminUser>
      </Card.Header>
      <Box margin="0 lg 0 lg">
        {isFetching || isLoading ? (
          <TableLoading rows={12} />
        ) : (
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={siteSurveys ?? []}
            isLoading={isLoading || isFetching}
            isScrollable={{ y: true }}
            hasStickyHeader
          />
        )}
      </Box>
    </>
  );
};
